import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { FieldSkeleton } from '@/components/layout/field-row'
import { Avatar } from '@/components/ui/avatar'

import { SIDE_NAV_STICKY_TOP, SIDE_NAV_WIDTH } from './side-nav.constants'

export const Layout = styled.div(({ theme }) => ({
  display: 'grid',
  flex: 1,
  gap: theme.space['16'],
  gridTemplateColumns: `${SIDE_NAV_WIDTH} 1fr`,
  minHeight: 0,
  overflow: 'clip',
}))

export const Content = styled.div<{ $scroll?: boolean }>(({ $scroll }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  minHeight: 0,
  overflowY: $scroll ? 'auto' : 'visible',
}))

export const Nav = styled.nav(({ theme }) => ({
  alignSelf: 'flex-start',
  background: theme.colors.white,
  border: `${theme.border.size['1']} solid ${theme.colors.gray500}`,
  borderRadius: theme.border.radius['12'],
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '100vh',
  overflowY: 'auto',
  position: 'sticky',
  top: SIDE_NAV_STICKY_TOP,
}))

export const HeaderCard = styled.div(({ theme }) => ({
  borderBottom: `${theme.border.size['1']} solid ${theme.colors.gray500}`,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['12'],
  padding: theme.space['16'],
}))

export const HeaderRow = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.space['16'],
}))

export const HeaderAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.colors.blue200,
  color: theme.colors.blue700,
  fontSize: '2rem',
  fontWeight: 600,
  height: '6.4rem',
  width: '6.4rem',
}))

// Note: same box and rounded-square shape as the loaded HeaderAvatar
export const HeaderAvatarSkeleton = styled(FieldSkeleton)(({ theme }) => ({
  borderRadius: theme.border.radius['8'],
  height: '6.4rem',
  width: '6.4rem',
}))

export const HeaderInfo = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['4'],
  minWidth: 0,
}))

export const BadgesRow = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: theme.space['8'],
}))

export const NavList = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['2'],
  padding: `${theme.space['8']} 0`,
}))

export const GroupHeader = styled.div(({ theme }) => ({
  background: theme.colors.gray200,
  color: theme.colors.primary600,
  fontSize: '1.2rem',
  fontWeight: 500,
  margin: `${theme.space['4']} 0`,
  padding: `${theme.space['6']} ${theme.space['20']}`,
}))

export const NavItem = styled(Link)<{ $active: boolean; $disabled?: boolean }>(
  ({ theme, $active, $disabled }) => ({
    '[data-component-id="Icon"]': {
      color: $disabled
        ? theme.colors.gray800
        : $active
          ? theme.colors.blue500
          : theme.colors.primary400,
    },
    '&::before': {
      backgroundColor: $active ? theme.colors.blue500 : 'transparent',
      borderRadius: '0 3px 3px 0',
      bottom: 0,
      content: '""',
      left: 0,
      position: 'absolute',
      top: 0,
      width: '3px',
    },
    '&:hover': {
      background: $disabled ? 'transparent' : $active ? theme.colors.gray200 : theme.colors.gray100,
      color: $disabled
        ? theme.colors.gray800
        : $active
          ? theme.colors.blue500
          : theme.colors.black800,
    },
    '&:hover [data-component-id="Icon"]': {
      color: $disabled
        ? theme.colors.gray800
        : $active
          ? theme.colors.blue500
          : theme.colors.primary400,
    },
    alignItems: 'center',
    background: $active ? theme.colors.gray200 : 'transparent',
    border: 'none',
    borderRadius: '0.6rem',
    color: $disabled
      ? theme.colors.gray800
      : $active
        ? theme.colors.blue500
        : theme.colors.black800,
    cursor: $disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    fontSize: '1.2rem',
    fontWeight: 500,
    gap: theme.space['4'],
    lineHeight: '1.6rem',
    margin: `0 ${theme.space['8']}`,
    overflow: 'hidden',
    padding: `${theme.space['8']} ${theme.space['12']}`,
    position: 'relative',
    textAlign: 'left' as const,
    textDecoration: 'none',
    transition: `background ${theme.transition.duration.sm} ease, color ${theme.transition.duration.sm} ease`,
  }),
)
